import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { Wallet, TrendingUp, TrendingDown, Clock, Package } from 'lucide-react';
import { useAlert } from '../context/AlertContext';
import API_BASE_URL from '../config/api';

const ShoppingWallet = () => {
    const { showAlert } = useAlert();
    const [walletData, setWalletData] = useState({ balance: 0, transactions: [] });
    const [loading, setLoading] = useState(true);

    const fetchWalletData = useCallback(async () => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/user/shopping_wallet.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setWalletData(data);
            }
        } catch (error) {
            showAlert('Failed to fetch wallet data', 'error');
        } finally {
            setLoading(false);
        }
    }, [showAlert]);

    useEffect(() => {
        fetchWalletData();
    }, [fetchWalletData]);

    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Shopping Wallet</h1>
                    <p className="text-gray-500 text-sm mt-1">Manage your shopping balance and transaction history</p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Balance Card */}
                    <div className="lg:col-span-2 space-y-6">
                        <div className="bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 rounded-2xl shadow-xl p-6 sm:p-8 text-white">
                            <div className="flex items-center justify-between mb-6">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 sm:p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                                        <Wallet size={20} className="sm:w-6 sm:h-6" />
                                    </div>
                                    <div>
                                        <p className="text-xs sm:text-sm text-white/80 font-medium">Shopping Balance</p>
                                        <p className="text-[10px] sm:text-xs text-white/60">Available for purchases</p>
                                    </div>
                                </div>
                            </div>
                            <div className="mb-8">
                                {loading ? (
                                    <div className="h-10 sm:h-12 bg-white/20 rounded-lg animate-pulse"></div>
                                ) : (
                                    <p className="text-3xl sm:text-5xl font-bold">₹ {walletData.balance.toLocaleString()}</p>
                                )}
                            </div>
                            <div className="grid grid-cols-2 gap-3 sm:gap-4">
                                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4">
                                    <p className="text-[10px] sm:text-xs text-white/70 mb-1">Total Packages</p>
                                    <p className="text-lg sm:text-xl font-bold">{walletData.package_breakdown?.length || 0}</p>
                                </div>
                                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4">
                                    <p className="text-[10px] sm:text-xs text-white/70 mb-1">Active Purchases</p>
                                    <p className="text-lg sm:text-xl font-bold">{walletData.package_breakdown?.filter(p => parseFloat(p.remaining_balance) > 0).length || 0}</p>
                                </div>
                            </div>
                        </div>

                        {/* Package Breakdown */}
                        {walletData.package_breakdown?.length > 0 && (
                            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                                <div className="p-4 sm:p-6 border-b border-gray-100 bg-gray-50/50">
                                    <h3 className="font-bold text-gray-900 flex items-center gap-2 text-sm sm:text-base">
                                        <Package size={18} className="text-indigo-600 sm:w-5 sm:h-5" />
                                        Your Packages & Spend Limits
                                    </h3>
                                </div>
                                <div className="divide-y divide-gray-100">
                                    {walletData.package_breakdown.map((pkg, idx) => (
                                        <div key={pkg.package_id || pkg.id || idx} className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:bg-gray-50 transition-colors">
                                            <div className="p-2 sm:p-3 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center">
                                                <Package size={20} />
                                            </div>
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <p className="font-bold text-gray-900 uppercase tracking-tight text-sm">{pkg.package_name}</p>
                                                    {pkg.instance_count > 1 && (
                                                        <span className="bg-blue-600 text-white text-[8px] font-black px-1.5 py-0.5 rounded-md uppercase tracking-tighter">
                                                            {pkg.instance_count} Combined
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-[10px] text-gray-400 font-bold uppercase py-0.5">LATEST ACTIVATION: {new Date(pkg.activated_at).toLocaleDateString()}</p>
                                            </div>
                                            <div className="text-left sm:text-right w-full sm:w-auto">
                                                <p className="font-black text-gray-900 text-sm sm:text-base">₹{parseFloat(pkg.remaining_balance).toLocaleString()}</p>
                                                <div className="w-full sm:w-24 h-1.5 bg-gray-100 rounded-full mt-1 overflow-hidden">
                                                    <div
                                                        className="h-full bg-indigo-500 rounded-full"
                                                        style={{ width: `${(parseFloat(pkg.remaining_balance) / parseFloat(pkg.price)) * 100}%` }}
                                                    ></div>
                                                </div>
                                                <p className="text-[9px] text-gray-400 font-bold mt-1 uppercase">OF ₹{parseFloat(pkg.price).toLocaleString()}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Transaction History */}
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                            <div className="p-4 sm:p-6 border-b border-gray-100">
                                <h3 className="font-bold text-gray-900 flex items-center gap-2 text-sm sm:text-base">
                                    <Clock size={18} className="text-gray-500 sm:w-5 sm:h-5" />
                                    Transaction History
                                </h3>
                            </div>
                            <div className="divide-y divide-gray-100">
                                {loading ? (
                                    <div className="p-8 text-center">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                                    </div>
                                ) : walletData.transactions.length === 0 ? (
                                    <div className="p-8 text-center">
                                        <Wallet className="mx-auto text-gray-300" size={48} />
                                        <p className="text-gray-500 mt-4 text-sm">No transactions yet</p>
                                    </div>
                                ) : (
                                    walletData.transactions.map((txn, idx) => (
                                        <div key={txn.id || idx} className="p-4 hover:bg-gray-50 transition-colors">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <div className={`p-2 sm:p-3 rounded-xl ${txn.transaction_type === 'credit'
                                                        ? 'bg-green-50 text-green-600'
                                                        : 'bg-red-50 text-red-600'
                                                        }`}>
                                                        {txn.transaction_type === 'credit' ? (
                                                            <TrendingUp size={16} className="sm:w-5 sm:h-5" />
                                                        ) : (
                                                            <TrendingDown size={16} className="sm:w-5 sm:h-5" />
                                                        )}
                                                    </div>
                                                    <div>
                                                        <p className="font-bold text-gray-900 text-xs sm:text-sm line-clamp-1">{txn.description}</p>
                                                        <p className="text-[10px] sm:text-xs text-gray-500">
                                                            {new Date(txn.created_at).toLocaleString()}
                                                        </p>
                                                        {txn.reference_id && (
                                                            <p className="text-[10px] text-gray-400 font-mono mt-0.5">
                                                                Ref: {txn.reference_id}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="text-right flex-shrink-0 ml-2">
                                                    <p className={`text-sm sm:text-lg font-bold ${txn.transaction_type === 'credit'
                                                        ? 'text-green-600'
                                                        : 'text-red-600'
                                                        }`}>
                                                        {txn.transaction_type === 'credit' ? '+' : '-'}₹{parseFloat(txn.amount).toLocaleString()}
                                                    </p>
                                                    <p className="text-[10px] sm:text-xs text-gray-500">
                                                        Bal: ₹{parseFloat(txn.balance_after).toLocaleString()}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Quick Access Sidebar */}
                    <div className="space-y-6">
                        <div className="bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl p-6 text-white shadow-lg sticky top-24">
                            <h4 className="font-bold text-lg mb-2">Need E-Pins?</h4>
                            <p className="text-xs sm:text-sm text-white/80 mb-4">
                                Request new E-Pins or manage your unused pins from the E-Pin System
                            </p>
                            <div className="space-y-3">
                                <button
                                    onClick={() => window.location.href = '/epin/apply'}
                                    className="w-full py-2 bg-white text-orange-600 font-bold rounded-lg text-sm hover:bg-orange-50 transition-colors shadow-sm"
                                >
                                    Apply New E-Pins
                                </button>
                                <button
                                    onClick={() => window.location.href = '/epin/unused'}
                                    className="w-full py-2 bg-orange-700/30 border border-white/20 text-white font-bold rounded-lg text-sm hover:bg-orange-700/50 transition-colors"
                                >
                                    Manage Unused Pins
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default ShoppingWallet;
